import { CheckCircle2, CircleAlert, Search } from "lucide-react";
import type { SeoScore } from "@/lib/seo-scorer";

interface BlogSeoScoreCardProps {
  score: SeoScore;
}

function scoreColor(value: number): string {
  if (value < 40) {
    return "var(--color-destructive)";
  }
  if (value < 70) {
    return "var(--color-warning)";
  }
  return "var(--color-success)";
}

export function BlogSeoScoreCard({ score }: BlogSeoScoreCardProps) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score.score / 100) * circumference;
  const ringColor = scoreColor(score.score);

  return (
    <div className="space-y-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 text-[var(--color-primary)]" />
        <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Live SEO score</h3>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative inline-flex h-[104px] w-[104px] items-center justify-center">
          <svg width="104" height="104" viewBox="0 0 104 104" aria-hidden="true">
            <circle
              cx="52"
              cy="52"
              r={radius}
              fill="none"
              stroke="var(--color-border)"
              strokeWidth="8"
            />
            <circle
              cx="52"
              cy="52"
              r={radius}
              fill="none"
              stroke={ringColor}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              transform="rotate(-90 52 52)"
              style={{ transition: "stroke-dashoffset 250ms ease" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-semibold" style={{ color: ringColor }}>
              {score.score}
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
              SEO
            </span>
          </div>
        </div>

        <div className="space-y-1 text-sm text-[var(--color-muted-foreground)]">
          <p>
            This score updates from the mounted article body plus the blog SEO metadata you edit here.
          </p>
          <p>
            Targets: red below 40, warning 40-69, strong 70+.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {score.rules.map((rule) => (
          <div
            key={rule.id}
            className="flex items-start gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2.5"
          >
            {rule.passed ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-success)]" />
            ) : (
              <CircleAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-warning)]" />
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-[var(--color-foreground)]">{rule.label}</p>
              <p className="text-xs text-[var(--color-muted-foreground)]">{rule.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
